import {
  Group,
  Grid,
  Image,
  Flex,
  Box,
  Text,
  Skeleton,
  useMantineTheme,
  Card,
  Title,
  Table,
  ActionIcon,
} from "@mantine/core";
import ActionCard from "src/components/ActionCard";
import {
  GetClientQuery,
  GetSignedAgreementsQuery,
  UserRole,
} from "src/generated/graphql";
import NotesList from "./notes";
import { TabsName } from ".";
import { IoIosArrowForward } from "react-icons/io";
import TransactionAttendance from "../TransactionAttendance";
import { useParams } from "react-router-dom";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";
export type ClientOneQuery = GetClientQuery["clientOne"];

interface ClientOverProps {
  clientData: ClientOneQuery | undefined;
  agreementData: GetSignedAgreementsQuery["signedAgreementMany"];
  onTabChange: (tabName: TabsName) => void;
  onRefetch: () => void;
}

interface HistoryTableData {
  date: string;
  info: string;
  location: string;
  status: string;
}
interface TableProps {
  columns: string[];
  data: HistoryTableData[];
}

export const transactionHistoryMock: HistoryTableData[] = [
  {
    date: "03/21/23",
    info: "$19.95",
    location: "Boise",
    status: "Paid",
  },
  {
    date: "02/08/23",
    info: "$8.73",
    location: "Boise",
    status: "Failed",
  },
  {
    date: "03/21/23",
    info: "$18.95",
    location: "Nampa",
    status: "Refunded",
  },
];

export const attendanceHistoryMock: HistoryTableData[] = [
  {
    date: "03/21/23",
    info: "Yoga",
    location: "Boise",
    status: "Paid",
  },
  {
    date: "02/08/23",
    info: "Yoga",
    location: "Boise",
    status: "Failed",
  },
  {
    date: "03/21/23",
    info: "Barre",
    location: "Nampa",
    status: "Refunded",
  },
];

export const EmptyData = ({ name = "data" }: { name?: string }) => (
  <Text fw="bold" align="center">
    No {name} found!
  </Text>
);

const ClientProfileOverView = ({
  clientData,
}: {
  clientData: Omit<NonNullable<ClientOneQuery>, "notes">;
}) => {
  const theme = useMantineTheme();

  const { user, dob } = clientData;
  let color = theme.colors.secondaryTextColor[6];

  const clientOverviewList = Object.entries({
    Name: `${user.firstName} ${user.middleName} ${user.lastName}`,
    Status: `${user.role === UserRole.Client ? "Member" : user.role}`,
    Email: user.email,
    DOB: dob,
    Phone: user.phoneNumber || "Number Not Added",
    "Last Login": user.lastLoginAt
      ? new Date(user.lastLoginAt).toDateString()
      : "User has never logged in",
  });
  return (
    <Grid>
      <Grid.Col span={3}>
        <Image
          src={clientData.user.profilePicUrl}
          radius="lg"
          alt="With default placeholder"
          withPlaceholder
          styles={{
            placeholder: {
              borderRadius: 24,
              height: 100,
            },
          }}
        />
      </Grid.Col>
      <Grid.Col span={9}>
        <Box>
          {clientOverviewList.map((clientEl, index) => (
            <Flex
              justify="space-between"
              align="center"
              mt={index > 0 ? 17 : 0}
              key={clientEl[1]}
              style={{ color }}
            >
              <Text fw="bold">{clientEl![0]}:</Text> <Text>{clientEl![1]}</Text>
            </Flex>
          ))}
        </Box>
      </Grid.Col>
    </Grid>
  );
};

export const AgreementsMap = ({
  agreements,
}: {
  agreements: GetSignedAgreementsQuery["signedAgreementMany"];
}) => {
  const theme = useMantineTheme();
  let primaryColor = theme.colors.primaryTextColor[6];
  let color = theme.colors.secondaryTextColor[6];

  return (
    <>
      {agreements.slice(0, 2).map((agreement) => (
        <Card
          key={agreement.id}
          style={{
            border: `1px solid ${theme.colors.primary[6]}`,
            color,
            overflow: "visible",
            backgroundColor: theme.colors.primary[2],
          }}
          radius="md"
          m={0}
          p={0}
          bg="transparent"
          mb={"xs"}
        >
          <Group
            p="6px 10px"
            style={{
              backgroundColor: theme.colors.primary[6],
              borderBottom: `1px solid ${theme.colors.primary[6]}`,
              borderRadius: "8px 8px 0 0",
              color: theme.colors.primaryTextColor[6],
            }}
          >
            <Title order={5}>{agreement.agreement.name}</Title>
          </Group>
          <Grid p="10px 10px 6px" style={{ color: primaryColor }}>
            <Grid.Col span={6}>
              <Group noWrap>
                <Text fw="bold">Status:</Text>
                <Text>Active</Text>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <Group noWrap>
                <Text fw="bold">Membership:</Text>{" "}
                <Text>{agreement.agreement.isMembership ? "Yes" : "No"}</Text>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <Group noWrap>
                <Text fw="bold">Last Payment:</Text> <Text>04/19/23</Text>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <Group noWrap>
                <Text fw="bold">Next Payment:</Text> <Text>05/19/23</Text>
              </Group>
            </Grid.Col>
          </Grid>
        </Card>
      ))}
    </>
  );
};

export const HistoryTable = ({ columns = [], data = [] }: TableProps) => {
  return (
    <Table striped highlightOnHover>
      <thead>
        <tr>
          {columns.map((column) => (
            <th>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((el, index) => (
          <tr key={index}>
            <td>{new Date(el.date).toDateString()}</td>
            <td>{el.info}</td>
            <td>{el.location}</td>
            <td>{el.status}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const ClientOverview = ({
  clientData,
  agreementData,
  onTabChange,
  onRefetch,
}: ClientOverProps) => {
  const { clientId } = useParams<{ clientId: string }>();
  const theme = useMantineTheme();
  if (!clientData || !agreementData) {
    return (
      <>
        <Skeleton height={50} circle mb="xl" />
        <Skeleton w="100%" height={10} mb={10} />
        <Skeleton w="100%" height={10} mb={10} />
        <Skeleton w="90%" height={10} mb={10} />
        <Skeleton w="80%" height={10} mb={10} />
        <Skeleton w="70%" height={10} mb={10} />
        <Skeleton w="50%" height={10} mb={10} />
        <Skeleton width="30%" height={10} radius="xl" />
      </>
    );
  }
  const { notes, ...restClientData } = clientData;
  return (
    <>
      <Group noWrap spacing="xs" align="stretch">
        <ActionCard
          title="Profile Info"
          rightIconButton={
            <ActionIcon
              onClick={() => onTabChange(TabsName.PROFILE)}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
            >
              <IoIosArrowForward />
            </ActionIcon>
          }
        >
          <ClientProfileOverView clientData={restClientData} />
        </ActionCard>
        <ActionCard
          title="Account"
          rightIconButton={
            <ActionIcon
              onClick={() => onTabChange(TabsName.ACCOUNT)}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
            >
              <IoIosArrowForward />
            </ActionIcon>
          }
        >
          {!agreementData || agreementData.length == 0 ? (
            <EmptyData name="agreements" />
          ) : (
            <AgreementsMap agreements={agreementData} />
          )}
        </ActionCard>
      </Group>
      <TransactionAttendance onTabChange={onTabChange} />
      <NotesList
        notes={notes}
        onRefetch={onRefetch}
        clientId={clientId}
        roleType={EMPLOYEE_ROLE_OBJ.clients}
      />
    </>
  );
};

export default ClientOverview;
